import React, { useState } from "react";
import { motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import { REEL_COVERS, driveThumb } from "@/lib/lumiqoData";
import MediaLightbox from "@/components/lumiqo/MediaLightbox";

const ReelCovers = () => {
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState(null);

  const handleOpen = (cover) => {
    setMedia({
      kind: "image",
      driveId: cover.driveId,
      title: cover.title,
      aspect: "9/16",
    });
    setOpen(true);
  };

  return (
    <section
      id="reel-covers"
      className="relative py-24 sm:py-32 bg-[#f5f6fa]"
      data-testid="reel-covers-section"
    >
      <div className="absolute inset-0 lq-grid-bg opacity-30 [mask-image:radial-gradient(circle_at_center,black,transparent_70%)]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 rounded-full bg-[#1d72ff]/10 text-[#1d72ff] text-xs font-semibold tracking-wide uppercase">
              Reel Covers
            </div>
            <h2
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0a1d4d] mt-5 leading-[1.05]"
              data-testid="reel-covers-heading"
            >
              Scroll-stopping <br />
              <span className="lq-text-gradient">reel covers</span>.
            </h2>
          </div>
          <p className="text-[#0a1d4d]/65 max-w-md text-base">
            Cover-first design system — every reel gets a hand-crafted cover
            that boosts grid retention and click-through.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
          {REEL_COVERS.map((cover, i) => (
            <motion.button
              key={cover.driveId}
              type="button"
              onClick={() => handleOpen(cover)}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className="group relative aspect-[9/16] rounded-2xl sm:rounded-3xl overflow-hidden bg-[#0a1d4d] border border-[#0a1d4d]/10 lq-shadow-soft hover:lq-shadow-glow transition-all duration-300"
              data-testid={`reel-cover-${i}`}
            >
              <img
                src={driveThumb(cover.driveId, 800)}
                alt={cover.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-90 transition-all duration-300">
                <Maximize2 className="w-4 h-4 text-[#0a1d4d]" />
              </div>
              <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-display font-semibold tracking-tight opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                {cover.title}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <MediaLightbox open={open} onOpenChange={setOpen} media={media} />
    </section>
  );
};

export default ReelCovers;
